import {
  BriefcaseBusiness,
  Building2,
  Handshake,
  History,
  House,
  Shapes,
  ShieldCheck,
  Smartphone,
  UserRound,
  Workflow
} from 'lucide-react'
import { AgencyCreatePanel } from '@/features/agencies/components/agency-create-panel'
import { AgencyDetailPanel } from '@/features/agencies/components/agency-detail-panel'
import { AgencyV2CreatePanel } from '@/features/agencies/components/agency-v2-create-panel'
import { AgencyV2DetailPanel } from '@/features/agencies/components/agency-v2-detail-panel'
import { ClientAccountV2DetailPanel } from '@/features/client-accounts/components/client-account-v2-detail-panel'
import { PortalFlowDetailPanel } from '@/features/client-portal/hub/portal-flow-detail-panel'
import { ClientCreateWizard } from '@/features/clients/components/client-create-wizard'
import { ClientCreateFlowForm } from '@/features/clients/components/client-detail/client-create-flow-form'
import { ClientDetailPanel } from '@/features/clients/components/client-detail/client-detail-panel'
import { ClientDetailV2Panel } from '@/features/clients/components/client-detail/client-detail-v2-panel'
import { CompanyCreatePanel } from '@/features/companies/components/company-create-panel'
import { CompanyDetailPanel } from '@/features/companies/components/company-detail-panel'
import { CompanyV2CreatePanel } from '@/features/companies/components/company-v2-create-panel'
import { CompanyV2DetailPanel } from '@/features/companies/components/company-v2-detail-panel'
import { CreditAuditLog } from '@/features/credits/components/credit-audit-log'
import {
  CreditDetailPanel,
  CreditDetailPanelFooter,
  creditDetailPanelIcon
} from '@/features/credits/components/credit-detail-panel'
import {
  DeliveryDetailPanel,
  deliveryDetailPanelIcon
} from '@/features/deliveries/components/delivery-detail-panel'
import { InventorySidePanelLoader } from '@/features/inventory/components/inventory-side-panel-content'
import { NegotiationDetailPanel } from '@/features/negotiations/components/negotiation-detail/negotiation-detail-panel'
import { ProfessionCreatePanel } from '@/features/professions/components/profession-create-panel'
import { ProfessionDetailPanel } from '@/features/professions/components/profession-detail-panel'
import { ProfessionV2CreatePanel } from '@/features/professions/components/profession-v2-create-panel'
import { ProfessionV2DetailPanel } from '@/features/professions/components/profession-v2-detail-panel'
import { ProjectCreatePanel } from '@/features/projects/components/project-create-panel'
import { ProjectDetailPanel } from '@/features/projects/components/project-detail-panel'
import { ProjectV2CreatePanel } from '@/features/projects/components/project-v2-create-panel'
import { ProjectV2DetailPanel } from '@/features/projects/components/project-v2-detail-panel'
import { PropertyCreatePanel } from '@/features/properties/components/property-create-panel'
import { PropertyDetailPanel } from '@/features/properties/components/property-detail-panel'
import { PropertyV2CreatePanel } from '@/features/properties/components/property-v2-create-panel'
import { PropertyV2DetailPanel } from '@/features/properties/components/property-v2-detail-panel'
import { RepresentativeCreatePanel } from '@/features/representatives/components/representative-create-panel'
import { RepresentativeDetailPanel } from '@/features/representatives/components/representative-detail-panel'
import { RepresentativeV2CreatePanel } from '@/features/representatives/components/representative-v2-create-panel'
import { RepresentativeV2DetailPanel } from '@/features/representatives/components/representative-v2-detail-panel'
import { RoleDetailPanel } from '@/features/roles/components/role-detail-panel'
import { RoleV2DetailPanel } from '@/features/roles/components/role-v2-detail-panel'
import { SocietyCreatePanel } from '@/features/societies/components/society-create-panel'
import { SocietyDetailPanel } from '@/features/societies/components/society-detail-panel'
import { SocietyV2CreatePanel } from '@/features/societies/components/society-v2-create-panel'
import { SocietyV2DetailPanel } from '@/features/societies/components/society-v2-detail-panel'
import { StateDetailPanel } from '@/features/states/components/state-detail-panel'
import { StateV2DetailPanel } from '@/features/states/components/state-v2-detail-panel'
import { TypePropertyCreatePanel } from '@/features/type-properties/components/type-property-create-panel'
import { TypePropertyDetailPanel } from '@/features/type-properties/components/type-property-detail-panel'
import { TypePropertyV2CreatePanel } from '@/features/type-properties/components/type-property-v2-create-panel'
import { TypePropertyV2DetailPanel } from '@/features/type-properties/components/type-property-v2-detail-panel'
import { UserCreatePanel } from '@/features/users/components/user-create-panel'
import { UserDetailPanel } from '@/features/users/components/user-detail-panel'
import { UserV2CreatePanel } from '@/features/users/components/user-v2-create-panel'
import { UserV2DetailPanel } from '@/features/users/components/user-v2-detail-panel'
import { WorkflowCreatePanel } from '@/features/workflows/components/workflow-create-panel'
import { WorkflowDetailPanel } from '@/features/workflows/components/workflow-detail-panel'
import { WorkflowV2CreatePanel } from '@/features/workflows/components/workflow-v2-create-panel'
import { WorkflowV2DetailPanel } from '@/features/workflows/components/workflow-v2-detail-panel'
import { formatCode } from '@/shared/constants/config'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import type { SidePanelPage, SidePanelPageDefinition } from './side-panel-types'

function ClientCreateSidePanelPage({ onDone }: { onDone?: () => void }) {
  const closePanel = useSidePanelStore((s) => s.closePanel)

  return <ClientCreateWizard onDone={onDone ?? closePanel} />
}

export const SIDE_PANEL_REGISTRY = {
  'inventory-detail': {
    title: (params) => params.propertyCode ?? 'Inmueble',
    icon: <House />,
    render: (params) => (
      <InventorySidePanelLoader
        propertyId={params.propertyId}
        onReserve={(item) => {
          params.onReserve?.(item)
        }}
        onRelease={(item) => {
          params.onRelease?.(item)
        }}
      />
    )
  },
  'client-detail': {
    title: (params) => `C-${String(params.client.code).padStart(8, '0')}`,
    icon: <UserRound />,
    render: (params) => <ClientDetailPanel client={params.client} />
  },
  'client-detail-v2': {
    title: (params) => `C-${String(params.client.code).padStart(8, '0')}`,
    icon: <UserRound />,
    render: (params) => <ClientDetailV2Panel client={params.client} />
  },
  'client-account-v2-detail': {
    title: () => 'Cuenta de la app',
    icon: <Smartphone />,
    render: (params) => <ClientAccountV2DetailPanel accountId={params.accountId} />
  },
  'client-create': {
    title: () => 'Nuevo cliente',
    icon: <UserRound />,
    render: (params) => <ClientCreateSidePanelPage onDone={params.onDone} />
  },
  'portal-flow-detail': {
    title: () => 'Detalle del flujo',
    icon: <Workflow />,
    render: (params) => (
      <PortalFlowDetailPanel
        flowId={params.flowId}
        clientId={params.clientId}
        clientName={params.clientName}
        clientType={params.clientType}
      />
    )
  },
  'negotiation-detail': {
    title: (params) =>
      params.negotiationCode ? formatCode(params.negotiationCode, 'negotiation') : 'Negociación',
    icon: <Handshake aria-hidden="true" />,
    render: (params) => <NegotiationDetailPanel negotiationId={params.negotiationId} />
  },
  'credit-detail': {
    // El título dice de qué se está mirando: el expediente cuando existe, y la negociación cuando
    // todavía no. Titular «Crédito» una fila sin trámite prometería algo que no hay.
    title: (params) =>
      params.row.credit
        ? `Crédito #${params.row.credit.code}`
        : formatCode(params.row.code, 'negotiation'),
    icon: creditDetailPanelIcon,
    render: (params) => <CreditDetailPanel row={params.row} />,
    footer: (params) => <CreditDetailPanelFooter row={params.row} />
  },
  'credit-audit': {
    title: (params) =>
      params.creditCode ? `Bitácora · Crédito #${params.creditCode}` : 'Bitácora del expediente',
    icon: <History aria-hidden="true" />,
    render: (params) => <CreditAuditLog creditId={params.creditId} />
  },
  'delivery-detail': {
    title: (params) => `Entrega · ${formatCode(params.row.code, 'negotiation')}`,
    icon: deliveryDetailPanelIcon,
    render: (params) => <DeliveryDetailPanel row={params.row} />
  },
  'property-detail': {
    title: (params) => params.property.code,
    icon: <House />,
    render: (params) => <PropertyDetailPanel property={params.property} />
  },
  'property-create': {
    title: () => 'Nuevo inmueble',
    icon: <House />,
    render: (params) => <PropertyCreatePanel onDone={params.onDone} />
  },
  'property-v2-detail': {
    title: (params) => params.property.code,
    icon: <House />,
    render: (params) => <PropertyV2DetailPanel property={params.property} />
  },
  'property-v2-create': {
    title: () => 'Nuevo inmueble',
    icon: <House />,
    render: (params) => <PropertyV2CreatePanel onDone={params.onDone} />
  },
  'user-detail': {
    title: (params) => `@${params.user.username}`,
    icon: <UserRound />,
    render: (params) => <UserDetailPanel user={params.user} />
  },
  'user-create': {
    title: () => 'Nuevo usuario',
    icon: <UserRound />,
    render: (params) => <UserCreatePanel onDone={params.onDone} />
  },
  'user-v2-detail': {
    title: (params) => `@${params.user.username}`,
    icon: <UserRound />,
    render: (params) => <UserV2DetailPanel user={params.user} />
  },
  'user-v2-create': {
    title: () => 'Nuevo usuario',
    icon: <UserRound />,
    render: (params) => <UserV2CreatePanel onDone={params.onDone} />
  },
  'profession-detail': {
    title: (params) => params.profession.name,
    icon: <BriefcaseBusiness />,
    render: (params) => <ProfessionDetailPanel profession={params.profession} />
  },
  'profession-create': {
    title: () => 'Nueva profesión',
    icon: <BriefcaseBusiness />,
    render: (params) => <ProfessionCreatePanel onDone={params.onDone} />
  },
  'profession-v2-detail': {
    title: (params) => params.profession.name,
    icon: <BriefcaseBusiness />,
    render: (params) => <ProfessionV2DetailPanel profession={params.profession} />
  },
  'profession-v2-create': {
    title: () => 'Nueva profesión',
    icon: <BriefcaseBusiness />,
    render: (params) => <ProfessionV2CreatePanel onDone={params.onDone} />
  },
  'type-property-detail': {
    title: (params) => params.typeProperty.code,
    icon: <Shapes />,
    render: (params) => <TypePropertyDetailPanel typeProperty={params.typeProperty} />
  },
  'type-property-create': {
    title: () => 'Nuevo subtipo',
    icon: <Shapes />,
    render: (params) => <TypePropertyCreatePanel onDone={params.onDone} />
  },
  'type-property-v2-detail': {
    title: (params) => params.typeProperty.name ?? params.typeProperty.code,
    icon: <Shapes />,
    render: (params) => <TypePropertyV2DetailPanel typeProperty={params.typeProperty} />
  },
  'type-property-v2-create': {
    title: () => 'Nuevo subtipo',
    icon: <Shapes />,
    render: (params) => <TypePropertyV2CreatePanel onDone={params.onDone} />
  },
  'project-detail': {
    title: (params) => params.project.name,
    icon: <Building2 />,
    render: (params) => <ProjectDetailPanel project={params.project} />
  },
  'project-create': {
    title: () => 'Nuevo proyecto',
    icon: <Building2 />,
    render: (params) => <ProjectCreatePanel onDone={params.onDone} />
  },
  'project-v2-detail': {
    title: (params) => params.project.name,
    icon: <Building2 />,
    render: (params) => <ProjectV2DetailPanel project={params.project} />
  },
  'project-v2-create': {
    title: () => 'Nuevo proyecto',
    icon: <Building2 />,
    render: (params) => <ProjectV2CreatePanel onDone={params.onDone} />
  },
  'society-detail': {
    title: (params) => params.society.name,
    icon: <Building2 />,
    render: (params) => <SocietyDetailPanel society={params.society} />
  },
  'society-create': {
    title: () => 'Nueva sociedad',
    icon: <Building2 />,
    render: (params) => <SocietyCreatePanel onDone={params.onDone} />
  },
  'society-v2-detail': {
    title: (params) => params.society.name,
    icon: <Building2 />,
    render: (params) => <SocietyV2DetailPanel society={params.society} />
  },
  'society-v2-create': {
    title: () => 'Nueva sociedad',
    icon: <Building2 />,
    render: (params) => <SocietyV2CreatePanel onDone={params.onDone} />
  },
  'representative-detail': {
    title: (params) => params.representative.name,
    icon: <UserRound />,
    render: (params) => <RepresentativeDetailPanel representative={params.representative} />
  },
  'representative-create': {
    title: () => 'Nuevo representante',
    icon: <UserRound />,
    render: (params) => <RepresentativeCreatePanel onDone={params.onDone} />
  },
  'representative-v2-detail': {
    title: (params) => params.representative.name,
    icon: <UserRound />,
    render: (params) => <RepresentativeV2DetailPanel representative={params.representative} />
  },
  'representative-v2-create': {
    title: () => 'Nuevo representante',
    icon: <UserRound />,
    render: (params) => <RepresentativeV2CreatePanel onDone={params.onDone} />
  },
  'company-detail': {
    title: (params) => params.company.name,
    icon: <Building2 />,
    render: (params) => <CompanyDetailPanel company={params.company} />
  },
  'company-create': {
    title: () => 'Nueva empresa',
    icon: <Building2 />,
    render: (params) => <CompanyCreatePanel onDone={params.onDone} />
  },
  'company-v2-detail': {
    title: (params) => params.company.name,
    icon: <Building2 />,
    render: (params) => <CompanyV2DetailPanel company={params.company} />
  },
  'company-v2-create': {
    title: () => 'Nueva empresa',
    icon: <Building2 />,
    render: (params) => <CompanyV2CreatePanel onDone={params.onDone} />
  },
  'agency-detail': {
    title: (params) => params.agency.name,
    icon: <Building2 />,
    render: (params) => <AgencyDetailPanel agency={params.agency} />
  },
  'agency-create': {
    title: () => 'Nueva agencia',
    icon: <Building2 />,
    render: (params) => <AgencyCreatePanel onDone={params.onDone} />
  },
  'agency-v2-detail': {
    title: (params) => params.agency.name,
    icon: <Building2 />,
    render: (params) => <AgencyV2DetailPanel agency={params.agency} />
  },
  'agency-v2-create': {
    title: () => 'Nueva agencia',
    icon: <Building2 />,
    render: (params) => <AgencyV2CreatePanel onDone={params.onDone} />
  },
  'state-detail': {
    title: (params) => params.state.name,
    icon: <Workflow />,
    render: (params) => <StateDetailPanel state={params.state} />
  },
  'state-v2-detail': {
    title: (params) => params.state.name,
    icon: <Workflow />,
    render: (params) => <StateV2DetailPanel state={params.state} />
  },
  'workflow-detail': {
    title: (params) => params.workflow.name,
    icon: <Workflow />,
    render: (params) => <WorkflowDetailPanel workflow={params.workflow} />
  },
  'workflow-create': {
    title: () => 'Nuevo flujo de trabajo',
    icon: <Workflow />,
    render: (params) => <WorkflowCreatePanel onDone={params.onDone} />
  },
  'workflow-v2-detail': {
    title: (params) => params.workflow.name,
    icon: <Workflow />,
    render: (params) => <WorkflowV2DetailPanel workflow={params.workflow} />
  },
  'workflow-v2-create': {
    title: () => 'Nuevo flujo de trabajo',
    icon: <Workflow />,
    render: (params) => <WorkflowV2CreatePanel onDone={params.onDone} />
  },
  'role-detail': {
    title: (params) => params.role.name,
    icon: <ShieldCheck />,
    render: (params) => <RoleDetailPanel role={params.role} />
  },
  'role-v2-detail': {
    title: (params) => params.role.name,
    icon: <ShieldCheck />,
    render: (params) => <RoleV2DetailPanel role={params.role} />
  },
  'portal-flow-create': {
    title: () => 'Nuevo flujo',
    icon: <Workflow />,
    render: (params) => (
      <div className="p-3">
        <ClientCreateFlowForm
          clientId={params.clientId}
          clientType={params.clientType}
          clientName={params.clientName}
          clientEmail={params.clientEmail}
        />
      </div>
    )
  }
} satisfies {
  [TPage in SidePanelPage]: SidePanelPageDefinition<TPage>
}

export function getSidePanelDefinition<TPage extends SidePanelPage>(
  page: TPage
): SidePanelPageDefinition<TPage> {
  return SIDE_PANEL_REGISTRY[page] as SidePanelPageDefinition<TPage>
}
